import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "../../lib/supabaseClient";
import "./TermDiscussion.css";

export default function TermDiscussionEdit({
    termId,
    title = "Community Moderation",
}) {
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");

    // includes deleted + non-deleted (admin view)
    const [comments, setComments] = useState([]);
    const [profilesByUserId, setProfilesByUserId] = useState({});

    // tools
    const [query, setQuery] = useState("");
    const [showDeleted, setShowDeleted] = useState(true); // admin can see deleted
    const [onlyDeleted, setOnlyDeleted] = useState(false);

    const loadTokenRef = useRef(0);

    const loadAll = useCallback(async () => {
        if (!termId) return;

        const myToken = ++loadTokenRef.current;
        setMsg("");
        setLoading(true);

        try {
            // Load ALL comments for moderation, including deleted
            const { data: cData, error: cErr } = await supabase
                .from("term_comments")
                .select(
                    "id, parent_id, user_id, body, is_deleted, is_removed_by_mod, moderated_at, moderation_reason, created_at",
                )
                .eq("term_submission_id", termId)
                .order("created_at", { ascending: false });

            if (cErr) throw cErr;
            if (loadTokenRef.current !== myToken) return;

            const list = cData ?? [];
            setComments(list);

            const userIds = Array.from(new Set(list.map((c) => c.user_id)));
            if (!userIds.length) {
                setProfilesByUserId({});
                return;
            }

            const { data: pData, error: pErr } = await supabase
                .from("profiles")
                .select("user_id, username, display_name, avatar_url")
                .in("user_id", userIds);

            if (pErr) throw pErr;
            if (loadTokenRef.current !== myToken) return;

            const map = {};
            (pData ?? []).forEach((p) => (map[p.user_id] = p));
            setProfilesByUserId(map);
        } catch (err) {
            if (loadTokenRef.current === myToken) {
                setMsg(err?.message || "Failed to load moderation view.");
            }
        } finally {
            if (loadTokenRef.current === myToken) setLoading(false);
        }
    }, [termId]);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // Filter list before building the tree
    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();

        return comments.filter((c) => {
            // visibility toggles
            if (!showDeleted && c.is_deleted) return false;
            if (onlyDeleted && !c.is_deleted) return false;

            if (!q) return true;

            const p = profilesByUserId[c.user_id];
            const author = (p?.display_name || p?.username || "").toLowerCase();
            const body = String(c.body || "").toLowerCase();
            const reason = String(c.moderation_reason || "").toLowerCase();

            return author.includes(q) || body.includes(q) || reason.includes(q);
        });
    }, [comments, profilesByUserId, query, showDeleted, onlyDeleted]);

    // Build nesting for context (moderation likes context)
    const tree = useMemo(() => {
        const children = new Map();
        for (const c of filtered) {
            const pid = c.parent_id ?? null;
            if (!children.has(pid)) children.set(pid, []);
            children.get(pid).push(c);
        }

        // Data already newest-first, keep it; no per-parent sort needed
        const build = (parentId) => {
            const list = children.get(parentId ?? null) ?? [];
            return list.map((c) => ({
                comment: c,
                replies: build(c.id),
            }));
        };

        return build(null);
    }, [filtered]);

    const adminRemove = async (commentId, reason) => {
        setMsg("");
        try {
            const { error } = await supabase.rpc("admin_remove_term_comment", {
                p_comment_id: commentId,
                p_reason: reason || null,
            });
            if (error) throw error;

            // Efficient: update local state
            setComments((prev) =>
                prev.map((c) =>
                    c.id === commentId
                        ? {
                              ...c,
                              is_deleted: true,
                              is_removed_by_mod: true,
                              body: "[removed]",
                              moderated_at: new Date().toISOString(),
                              moderation_reason: reason || null,
                          }
                        : c,
                ),
            );
        } catch (err) {
            setMsg(err?.message || "Failed to remove comment.");
        }
    };

    const adminRestore = async (commentId) => {
        setMsg("");
        try {
            const { error } = await supabase.rpc("admin_restore_term_comment", {
                p_comment_id: commentId,
            });
            if (error) throw error;

            // Efficient: safest is to reload because body may restore from original_body
            await loadAll();
        } catch (err) {
            setMsg(err?.message || "Failed to restore comment.");
        }
    };

    if (!termId) return null;

    return (
        <div className="td-wrap">
            <div className="td-header">
                <div>
                    <h3 className="td-title">{title}</h3>
                    <p className="td-desc">
                        Admin-only: remove + restore. Public users never see
                        deleted comments.
                    </p>
                </div>

                <div className="td-controls">
                    <button
                        type="button"
                        className="td-refresh"
                        onClick={loadAll}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>
            </div>

            <div className="td-modbar">
                <input
                    className="td-modsearch"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search author/body/reason…"
                />

                <label className="td-modcheck">
                    <input
                        type="checkbox"
                        checked={showDeleted}
                        onChange={(e) => {
                            const v = e.target.checked;
                            setShowDeleted(v);
                            if (!v) setOnlyDeleted(false);
                        }}
                    />
                    Show deleted
                </label>

                <label className="td-modcheck">
                    <input
                        type="checkbox"
                        checked={onlyDeleted}
                        onChange={(e) => {
                            const v = e.target.checked;
                            setOnlyDeleted(v);
                            if (v) setShowDeleted(true);
                        }}
                    />
                    Only deleted
                </label>
            </div>

            {msg && <div className="td-msg">{msg}</div>}

            <div className="td-list">
                {tree.length === 0 ? (
                    <div className="td-empty">No comments match filters.</div>
                ) : (
                    tree.map((node) => (
                        <ModCommentNode
                            key={node.comment.id}
                            node={node}
                            profilesByUserId={profilesByUserId}
                            onRemove={adminRemove}
                            onRestore={adminRestore}
                            depth={0}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function ModCommentNode({
    node,
    profilesByUserId,
    onRemove,
    onRestore,
    depth,
}) {
    const c = node.comment;

    const p = profilesByUserId[c.user_id];
    const author =
        p?.display_name ||
        p?.username ||
        (c.user_id ? c.user_id.slice(0, 8) : "Unknown");
    const avatar = p?.avatar_url || "";

    const [reason, setReason] = useState("");

    return (
        <div className="td-comment" style={{ marginLeft: depth * 14 }}>
            <div className="td-comment-main">
                <div className="td-votes td-votes--empty" />

                <div className="td-body">
                    <div className="td-meta">
                        {avatar ? (
                            <img
                                className="td-avatar"
                                src={avatar}
                                alt=""
                                loading="lazy"
                            />
                        ) : (
                            <div className="td-avatar td-avatar--empty" />
                        )}

                        <span className="td-author">{author}</span>
                        <span className="td-dot">•</span>
                        <span className="td-time">
                            {new Date(c.created_at).toLocaleString()}
                        </span>

                        {c.is_deleted ? (
                            <>
                                <span className="td-dot">•</span>
                                <span className="td-modtag">
                                    {c.is_removed_by_mod
                                        ? "Removed"
                                        : "Deleted"}
                                </span>
                            </>
                        ) : null}
                    </div>

                    <div
                        className={
                            "td-text " + (c.is_deleted ? "is-deleted" : "")
                        }
                    >
                        {c.body}
                    </div>

                    {c.moderated_at || c.moderation_reason ? (
                        <div className="td-modmeta">
                            <div>
                                <strong>Moderated:</strong>{" "}
                                {c.moderated_at
                                    ? new Date(c.moderated_at).toLocaleString()
                                    : "—"}
                            </div>
                            <div>
                                <strong>Reason:</strong>{" "}
                                {c.moderation_reason || "—"}
                            </div>
                        </div>
                    ) : null}

                    <div className="td-modpanel">
                        {!c.is_deleted ? (
                            <>
                                <input
                                    className="td-modreason"
                                    value={reason}
                                    onChange={(e) => setReason(e.target.value)}
                                    placeholder="Reason (optional)"
                                />
                                <button
                                    type="button"
                                    className="td-modbtn td-modremove"
                                    onClick={() => onRemove(c.id, reason)}
                                >
                                    Remove
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="td-modbtn td-modrestore"
                                onClick={() => onRestore(c.id)}
                            >
                                Restore
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {node.replies?.length ? (
                <div className="td-replies">
                    {node.replies.map((child) => (
                        <ModCommentNode
                            key={child.comment.id}
                            node={child}
                            profilesByUserId={profilesByUserId}
                            onRemove={onRemove}
                            onRestore={onRestore}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
